/**
 * Hooks de chat direto (M8). Sao uma fina camada sobre /threads
 * filtrando por ``type=chat`` -- reusa o backend de threads existente
 * em vez de duplicar endpoints.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type { Thread, ThreadCreate } from "@/api/types";

const CHAT_KEY = ["threads", "chat"] as const;

/** Lista threads do user filtradas por type=chat (mais recentes primeiro). */
export function useChatThreads() {
  return useQuery({
    queryKey: CHAT_KEY,
    queryFn: async () => {
      // Backend nao tem filtro por type ainda em GET /threads; pegamos
      // tudo e filtramos client-side. Volume baixo (chats sao do user).
      const all = await api.GET<Thread[]>("/threads");
      return all.filter((t) => t.type === "chat");
    },
    staleTime: 30_000,
  });
}

/** Cria thread type=chat com llm_model_id obrigatorio. */
export function useCreateChatThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { llm_model_id: string; title?: string }) => {
      const body: ThreadCreate = {
        type: "chat",
        llm_model_id: payload.llm_model_id,
        title: payload.title,
      };
      return api.POST<Thread>("/threads", body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHAT_KEY });
      void qc.invalidateQueries({ queryKey: ["threads"] });
    },
  });
}

/** Troca modelo de uma thread chat existente (Claude.ai style). */
export function useUpdateChatThreadModel(threadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (llm_model_id: string) =>
      api.PATCH<Thread>(`/threads/${threadId}/llm-model`, { llm_model_id }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHAT_KEY });
      void qc.invalidateQueries({ queryKey: ["threads", threadId] });
    },
  });
}

/** Deleta thread chat (soft-delete no backend). */
export function useDeleteChatThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (threadId: string) =>
      api.DELETE<void>(`/threads/${threadId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHAT_KEY });
      void qc.invalidateQueries({ queryKey: ["threads"] });
    },
  });
}
